import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Grid, Typography, Paper, Box } from "@mui/material";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  TableFooter,
  TablePagination,
  Toolbar,
} from "@mui/material";
import { usePDF } from "react-to-pdf";
import * as XLSX from "xlsx";
import { StyledButton, StyledCancelButton } from "components/StyledComponents/styledComponents";
import { format, differenceInMonths, differenceInDays } from "date-fns";
import BusinessSummaryBar from "./businessSummaryBar.js";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import URLs from "constants/urls.js";
import Cookie from "js-cookie";
import ExpenseBreakdownDialog from "./expenseBreakdownDailogue.js";
import DmaPayoutsDialog from "./dmaPayoutsDialog.js";

export default function ExpenditureIncomeReport({
  data,
  isIncomeByMis = false,
  startDate,
  endDate,
  setAlert,
  selectedDsaId,
}) {
  const { toPDF, targetRef } = usePDF({
    filename: "Expenditure_Income_Report.pdf",
    options: {
      method: "save",
      resolution: 3,
      page: { margin: 10, format: "A4", orientation: "portrait" },
      canvas: { mimeType: "image/png", qualityRatio: 1 },
    },
  });
  const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);
  const [breakdownData, setBreakdownData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLedger, setSelectedLedger] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const jwt_token = Cookie.get("jwt_token");
  const [dmaPayoutDialogOpen, setDmaPayoutDialogOpen] = useState(false);
  const [dmaPayoutData, setDmaPayoutData] = useState([]);
  const [dmaPayoutLoading, setDmaPayoutLoading] = useState(false);
  const [dmaPayoutError, setDmaPayoutError] = useState(null);
  useEffect(() => {
    setPage(0);
  }, [breakdownData]);

  const handleBreakdownClick = async (ledgerName) => {
    setSelectedLedger(ledgerName);
    setBreakdownDialogOpen(true);
    setLoading(true);
    setError(null);

    try {
      if (!jwt_token) {
        throw new Error("Authentication token not found");
      }

      // Use dates directly if already in YYYY-MM-DD
      const formattedStartDate = startDate || "";
      const formattedEndDate = endDate || "";

      const url =
        `${URLs.byfurcationOfExpenses}` +
        `?ledgerName=${encodeURIComponent(ledgerName)}` +
        `&startDate=${formattedStartDate}` +
        `&endDate=${formattedEndDate}` +
        `&companyId=${selectedDsaId ?? 0}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        // removed credentials: "include"
      });

      if (!response.ok) {
        const text = await response.text(); // helpful debug
        throw new Error(`API failed (${response.status}): ${text}`);
      }

      const data = await response.json();
      setBreakdownData(data || []);
    } catch (err) {
      console.error("Breakdown API error:", err);
      setError(err.message || "Failed to fetch breakdown data");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setBreakdownDialogOpen(false);
    setBreakdownData([]);
    setError(null);
    setPage(0);
    setRowsPerPage(10);
  };

  const handleDmaPayoutsClick = async () => {
    setDmaPayoutDialogOpen(true);
    setDmaPayoutLoading(true);
    setDmaPayoutError(null);

    try {
      if (!jwt_token) {
        throw new Error("Authentication token not found");
      }

      const formattedStartDate = startDate || "";
      const formattedEndDate = endDate || "";
      const url =
        `${URLs.dmaPayoutBreakdown}` +
        `?startDate=${formattedStartDate}` +
        `&endDate=${formattedEndDate}` +
        `&companyId=${selectedDsaId ?? 0}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`API failed (${response.status}): ${text}`);
      }

      const data = await response.json();
      setDmaPayoutData(data || []);
    } catch (err) {
      console.error("DMA Payouts API error:", err);
      setDmaPayoutError(err.message || "Failed to fetch DMA Payouts data");
    } finally {
      setDmaPayoutLoading(false);
    }
  };

  const handleCloseDmaPayoutDialog = () => {
    setDmaPayoutDialogOpen(false);
    setDmaPayoutData([]);
    setDmaPayoutError(null);
    // Reset pagination if you add it to DMA dialog
  };

  // Calculate total for DMA Payouts
  const calculateDmaPayoutTotal = () => {
    return dmaPayoutData.reduce((sum, item) => sum + (parseFloat(item.totalInvoiceAmount) || 0), 0);
  };
  const handleExcelExport = () => {
    const expSheet = data.expenseDetails.map((e, idx) => [idx + 1, e.particulars, e.amount]);
    const incSheet = data.businessBreakdown.map((i, idx) => [idx + 1, i.particulars, i.amount]);

    const maxRows = Math.max(expSheet.length, incSheet.length);
    while (expSheet.length < maxRows) expSheet.push(["", "", ""]);
    while (incSheet.length < maxRows) incSheet.push(["", "", ""]);

    const finalData = [["#", "Expenditure", "Amount", "#", "Income", "Amount"]];
    for (let i = 0; i < maxRows; i++) {
      finalData.push([...expSheet[i], ...incSheet[i]]);
    }
    finalData.push(["", "Total", data.totalExpenses.amount, "", "Total", data.totalEstRev.amount]);
    finalData.push([
      "",
      data.estimatedNetProfit.amount >= 0 ? "Profit" : "Loss",
      Math.abs(data.estimatedNetProfit.amount),
      "",
      "",
      "",
    ]);

    const ws = XLSX.utils.aoa_to_sheet(finalData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, "Expenditure_Income_Report.xlsx");
  };

  return (
    <Paper sx={{ p: 3 }} ref={targetRef} elevation={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
        <Typography variant="h6" gutterBottom>
          {isIncomeByMis
            ? "Expenditure and Income Report A/C to (MIS)"
            : "Expenditure and Income A/C to Account Statement"}
          <br />
          <span
            style={{
              backgroundColor: "#F5F5F5",
              color: "black",
              fontWeight: 600,
              borderRadius: "10px",
              padding: "4px 12px",
              display: "inline-block",
              cursor: "pointer",
              border: "1px solid #ccc",
              transition: "background-color 0.3s ease",
            }}
          >
            From {format(new Date(startDate), "dd-MMM-yyyy")} &nbsp;to&nbsp;
            {format(new Date(endDate), "dd-MMM-yyyy")} &nbsp; (
            {(() => {
              const months = differenceInMonths(new Date(endDate), new Date(startDate));
              const days = differenceInDays(new Date(endDate), new Date(startDate)) % 30;

              if (months > 0 && days > 0) {
                return `${months} month${months > 1 ? "s" : ""} ${days} day${days > 1 ? "s" : ""}`;
              } else if (months > 0) {
                return `${months} month${months > 1 ? "s" : ""}`;
              } else {
                return `${days} day${days > 1 ? "s" : ""}`;
              }
            })()}
            )
          </span>
        </Typography>

        <Box sx={{ textAlign: "center", mb: 2 }}>
          {/* Screen-only: show buttons */}
          <Box>
            <StyledButton variant="contained" sx={{ mr: 1 }} onClick={() => toPDF()}>
              Download PDF
            </StyledButton>
            <StyledCancelButton variant="outlined" onClick={handleExcelExport}>
              Download Excel
            </StyledCancelButton>
          </Box>
        </Box>
      </Box>

      {/* Table Header */}
      {!isIncomeByMis && (
        <>
          {" "}
          <Grid container sx={{ border: "1px solid #ccc" }}>
            {/* Heading Row */}
            <Grid container>
              <Grid
                item
                xs={6}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ display: "flex", alignItems: "center", fontWeight: "bold" }}
                >
                  Expenditure
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
                sx={{
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ display: "flex", alignItems: "center", fontWeight: "bold" }}
                >
                  Income
                </Typography>
              </Grid>
            </Grid>

            {/* Column Headers */}
            {/* Expense columns */}
            <Grid
              item
              xs={1}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                background: "#f5f5f5",
                textAlign: "center",
              }}
            >
              <Typography variant="subtitle2">
                <strong>S.No</strong>
              </Typography>
            </Grid>
            <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5" }}>
              <Typography variant="subtitle2">
                <strong>Particulars</strong>
              </Typography>
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                background: "#f5f5f5",
                textAlign: "right",
              }}
            >
              <Typography variant="subtitle2">
                <strong>Amount</strong>
              </Typography>
            </Grid>

            {/* Income columns */}
            <Grid
              item
              xs={1}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                background: "#f5f5f5",
                textAlign: "center",
              }}
            >
              <Typography variant="subtitle2">
                <strong>S.No</strong>
              </Typography>
            </Grid>
            <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5" }}>
              <Typography variant="subtitle2">
                <strong>Particulars</strong>
              </Typography>
            </Grid>
            <Grid item xs={2} sx={{ p: 1, background: "#f5f5f5", textAlign: "right" }}>
              <Typography variant="subtitle2">
                <strong>Amount</strong>
              </Typography>
            </Grid>
          </Grid>
          {/* Table Body */}
          {(() => {
            const maxLength = Math.max(data.expenseDetails.length, data.businessBreakdown.length);
            const expRows = data.expenseDetails;
            const incRows = data.businessBreakdown;

            return Array.from({ length: maxLength }).map((_, idx) => (
              <Grid
                container
                key={idx}
                sx={{ borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc" }}
              >
                {/* Expense */}
                <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "14px" }}>{expRows[idx] ? idx + 1 : ""}</Typography>
                </Grid>
                <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1 }}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {expRows[idx]?.particulars || ""}
                    {expRows[idx]?.particulars && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          // Check if this is DMA Payouts
                          if (expRows[idx].particulars.trim().toUpperCase() === "DMA PAYOUTS") {
                            handleDmaPayoutsClick();
                          } else {
                            handleBreakdownClick(expRows[idx].particulars);
                          }
                        }}
                        sx={{
                          ml: 1,
                          color: "primary.main",
                          "&:hover": {
                            backgroundColor: "rgba(255, 152, 0, 0.1)",
                          },
                        }}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={2} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "right" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {expRows[idx]?.amount != null ? Number(expRows[idx].amount).toFixed(2) : ""}
                  </Typography>
                </Grid>

                {/* Income */}
                <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "14px" }}>{incRows[idx] ? idx + 1 : ""}</Typography>
                </Grid>
                <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1 }}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {incRows[idx]?.particulars || ""}
                  </Typography>
                </Grid>
                <Grid item xs={2} sx={{ p: 1, textAlign: "right" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {incRows[idx]?.amount != null ? Number(incRows[idx].amount).toFixed(2) : ""}
                  </Typography>
                </Grid>
              </Grid>
            ));
          })()}
          <Grid
            container
            sx={{ border: "1px solid #ccc", borderTop: "none", background: "#f5f5f5" }}
          >
            <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }}></Grid>
            <Grid
              item
              xs={3}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              Total
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                fontSize: "1rem",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              {data.totalExpenses?.amount != null
                ? Number(data.totalExpenses.amount).toFixed(2)
                : ""}
            </Grid>

            <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }}></Grid>
            <Grid
              item
              xs={3}
              sx={{
                fontSize: "1rem",
                fontWeight: "bold",
                borderRight: "1px solid #ccc",
                p: 1,
              }}
            >
              Total
            </Grid>
            <Grid
              item
              xs={2}
              sx={{ p: 1, fontSize: "1rem", textAlign: "right", fontWeight: "bold" }}
            >
              {data.totalEstRev?.amount != null ? Number(data.totalEstRev.amount).toFixed(2) : ""}
            </Grid>

            <Grid item xs={6}></Grid>
            <Grid item xs={1}></Grid>
            <Grid
              item
              xs={3}
              sx={{
                p: 1,
                border: "1px solid #ccc",
                fontSize: "1rem",
                textAlign: "left",
                fontWeight: "bold",
              }}
            >
              Income
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                p: 1,
                border: "1px solid #ccc",
                borderRight: "0px solid #ccc",
                fontSize: "1rem",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              {data.income != null ? Number(data.income).toFixed(2) : ""}
            </Grid>
          </Grid>
        </>
      )}
      {isIncomeByMis && (
        <>
          <Grid container sx={{ border: "1px solid #ccc" }}>
            <BusinessSummaryBar
              data={data}
              startDate={startDate}
              endDate={endDate}
              isIncomeByMis={isIncomeByMis}
              setAlert={setAlert}
              selectedDsaId={selectedDsaId}
            />
            {/* Header Row */}
            <Grid container>
              <Grid
                item
                xs={4}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Expenditure
                </Typography>
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Received Income
                </Typography>
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Not Received Income
                </Typography>
              </Grid>
            </Grid>

            {/* Column Headers */}
            {["Expenditure", "Received", "NotReceived"].map((type, idx) => (
              <React.Fragment key={type}>
                <Grid
                  item
                  xs={1}
                  sx={{
                    borderRight: "1px solid #ccc",
                    p: 1,
                    background: "#f5f5f5",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="subtitle2">
                    <strong>S.No</strong>
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={2}
                  sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5" }}
                >
                  <Typography variant="subtitle2">
                    <strong>Particulars</strong>
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={1}
                  sx={{
                    borderRight: idx === 2 ? "0px" : "1px solid #ccc",
                    p: 1,
                    background: "#f5f5f5",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="subtitle2">
                    <strong>Amount</strong>
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>

          {/* Table Body */}
          {(() => {
            const expRows = data.expenseDetails || [];
            const recRows = data.receivedIncome || [];
            const notRecRows = data.notReceivedIncome || [];

            const maxLength = Math.max(expRows.length, recRows.length, notRecRows.length);

            return Array.from({ length: maxLength }).map((_, idx) => (
              <Grid
                container
                key={idx}
                sx={{ borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc" }}
              >
                {/* Expenditure */}
                <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "14px" }}>{expRows[idx] ? idx + 1 : ""}</Typography>
                </Grid>
                <Grid item xs={2} sx={{ borderRight: "1px solid #ccc", p: 1 }}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {expRows[idx]?.particulars || ""}
                    {expRows[idx]?.particulars && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          // Clean and normalize the string for comparison
                          const particulars = expRows[idx].particulars.trim();

                          // Case-insensitive comparison
                          if (particulars.toLowerCase() === "dma payouts") {
                            handleDmaPayoutsClick();
                          } else {
                            handleBreakdownClick(particulars);
                          }
                        }}
                        sx={{
                          ml: 1,
                          color: "primary.main",
                          "&:hover": {
                            backgroundColor: "rgba(255, 152, 0, 0.1)",
                          },
                        }}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "right" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {expRows[idx]?.amount != null ? Number(expRows[idx].amount).toFixed(2) : ""}
                  </Typography>
                </Grid>

                {/* Received Income */}
                <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "14px" }}>{recRows[idx] ? idx + 1 : ""}</Typography>
                </Grid>
                <Grid item xs={2} sx={{ borderRight: "1px solid #ccc", p: 1 }}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {recRows[idx]?.particulars || ""}
                  </Typography>
                </Grid>
                <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "right" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {recRows[idx]?.amount != null ? Number(recRows[idx].amount).toFixed(2) : ""}
                  </Typography>
                </Grid>

                {/* Not Received Income */}
                <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {notRecRows[idx] ? idx + 1 : ""}
                  </Typography>
                </Grid>
                <Grid item xs={2} sx={{ borderRight: "1px solid #ccc", p: 1 }}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {notRecRows[idx]?.particulars || ""}
                  </Typography>
                </Grid>
                <Grid item xs={1} sx={{ p: 1, textAlign: "right" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {notRecRows[idx]?.amount != null
                      ? Number(notRecRows[idx].amount).toFixed(2)
                      : ""}
                  </Typography>
                </Grid>
              </Grid>
            ));
          })()}

          {/* Totals Section */}
          <Grid
            container
            sx={{ border: "1px solid #ccc", borderTop: "none", background: "#f5f5f5" }}
          >
            {/* Expenditure Total */}
            <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }}></Grid>
            <Grid
              item
              xs={2}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              Total
            </Grid>
            <Grid
              item
              xs={1}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                fontSize: "1rem",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              {data.finalExpenseTotal?.amount != null
                ? Number(data.finalExpenseTotal.amount).toFixed(2)
                : ""}
            </Grid>

            {/* Received Total */}
            <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }}></Grid>
            <Grid
              item
              xs={2}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              Total
            </Grid>
            <Grid
              item
              xs={1}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                fontSize: "1rem",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              {data.finalIncomeTotal?.amount != null
                ? Number(data.finalIncomeTotal.amount).toFixed(2)
                : ""}
            </Grid>

            <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }}></Grid>
            <Grid
              item
              xs={2}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              Not Received Total
            </Grid>
            <Grid
              item
              xs={1}
              sx={{
                p: 1,
                fontSize: "1rem",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              {data.notReceivedAmount?.amount != null
                ? Number(data.notReceivedAmount.amount).toFixed(2)
                : ""}
            </Grid>
          </Grid>
        </>
      )}
      <ExpenseBreakdownDialog
        open={breakdownDialogOpen}
        onClose={handleCloseDialog}
        startDate={startDate}
        endDate={endDate}
        selectedLedger={selectedLedger}
        breakdownData={breakdownData}
        loading={loading}
        error={error}
      />
      <DmaPayoutsDialog
        open={dmaPayoutDialogOpen}
        onClose={handleCloseDmaPayoutDialog}
        data={dmaPayoutData}
        loading={dmaPayoutLoading}
        error={dmaPayoutError}
        startDate={startDate}
        endDate={endDate}
      />
    </Paper>
  );
}

ExpenditureIncomeReport.propTypes = {
  data: PropTypes.shape({
    income: PropTypes.number,
    expenseDetails: PropTypes.arrayOf(
      PropTypes.shape({
        month: PropTypes.string,
        particulars: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
      })
    ).isRequired,
    totalExpenses: PropTypes.shape({
      month: PropTypes.string,
      particulars: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }).isRequired,
    businessBreakdown: PropTypes.arrayOf(
      PropTypes.shape({
        month: PropTypes.string,
        particulars: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
      })
    ).isRequired,
    totalEstRev: PropTypes.shape({
      month: PropTypes.string,
      particulars: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }).isRequired,
    estimatedNetProfit: PropTypes.shape({
      month: PropTypes.string,
      particulars: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }).isRequired,

    //  New fields for Received / Not Received Income
    receivedIncome: PropTypes.arrayOf(
      PropTypes.shape({
        particulars: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
      })
    ),
    notReceivedIncome: PropTypes.arrayOf(
      PropTypes.shape({
        particulars: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
      })
    ),
    totalReceivedRevenue: PropTypes.shape({
      particulars: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }),
    notReceivedAmount: PropTypes.shape({
      particulars: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }),
    finalExpenseTotal: PropTypes.shape({
      particulars: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }),
    finalIncomeTotal: PropTypes.shape({
      particulars: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }),
  }).isRequired,

  startDate: PropTypes.string,
  endDate: PropTypes.string,
  isIncomeByMis: PropTypes.bool,
  setAlert: PropTypes.func,
  selectedDsaId: PropTypes.number,
  selectedBusinessId: PropTypes.number,
  count: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  handleChangePage: PropTypes.func,
  handleChangeRowsPerPage: PropTypes.func,
  onPageChange: PropTypes.func,
};
